import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import PassagePicker from '../components/PassagePicker';
import DashboardHeader from '../components/DashboardHeader';
import { useAutosizeTextarea } from '../hooks/useAutosizeTextarea';
import { passageIdFromLocationSearch } from '../utils/passageUrl';
import './Dashboard.css';

const LINE_TEXTAREA_STYLE = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 14,
  lineHeight: 1.5,
};

const MIN_TEXTAREA_HEIGHT_PX = 120;

const textInputStyle = {
  ...LINE_TEXTAREA_STYLE,
  width: '100%',
  boxSizing: 'border-box',
};

const rowShellStyle = {
  marginBottom: 24,
  paddingBottom: 20,
  borderBottom: '1px solid #e0e0e0',
};

const titleLabelStyle = {
  display: 'block',
  margin: '0 0 8px',
  fontSize: 16,
  fontWeight: 600,
};

function SubFieldLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} style={{ display: 'block', margin: '0 0 6px', fontWeight: 600, fontSize: 14 }}>
      {children}
    </label>
  );
}

function AutosizeMonoTextarea({ id, value, onChange, disabled, ariaLabel }) {
  const taRef = useRef(null);

  const sharedStyle = {
    ...LINE_TEXTAREA_STYLE,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    resize: 'none',
    minHeight: MIN_TEXTAREA_HEIGHT_PX,
    minWidth: 0,
  };

  const measure = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const h = Math.max(el.scrollHeight, MIN_TEXTAREA_HEIGHT_PX);
    const nextHeight = `${h}px`;
    if (el.style.height !== nextHeight) {
      el.style.height = nextHeight;
    }
    el.style.overflow = 'hidden';
  }, []);

  useAutosizeTextarea(measure, [value]);

  return (
    <textarea
      ref={taRef}
      id={id}
      value={value}
      rows={1}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={sharedStyle}
      aria-label={ariaLabel}
    />
  );
}

const normalizeRow = (r) => ({
  sentence_id: r.sentence_id,
  order: r.order,
  value: r.value,
  translation: r.translation,
  question: r.question == null ? '' : String(r.question),
  correct: r.correct == null ? '' : String(r.correct),
  wrong_1: r.wrong_1 == null ? '' : String(r.wrong_1),
  wrong_2: r.wrong_2 == null ? '' : String(r.wrong_2),
  level: r.level != null && r.level !== '' && !Number.isNaN(Number(r.level)) ? Number(r.level) : 1,
  question_id: r.question_id,
});

const GENERATE_BATCH_SIZE = 8;

const hasTranslation = (r) => {
  if (r.translation == null) return false;
  return String(r.translation).trim() !== '';
};

const mergeGeneratedRows = (prev, generatedRows) => {
  const generatedById = new Map(generatedRows.map((r) => [r.sentence_id, r]));
  return prev.map((p) => {
    const generated = generatedById.get(p.sentence_id);
    if (!generated) {
      return p;
    }
    if (generated.generated === false) {
      return p;
    }
    return {
      ...normalizeRow(generated),
      level: p.level,
      question_id: p.question_id != null ? p.question_id : generated.question_id,
    };
  });
};

const TranslationQuestion = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passagesLoading, setPassagesLoading] = useState(true);
  const [passagesError, setPassagesError] = useState('');
  const [passageId, setPassageId] = useState(passageIdFromLocationSearch);
  const [rows, setRows] = useState([]);
  const [loadLoading, setLoadLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPassagesLoading(true);
      setPassagesError('');
      try {
        const res = await api.get('/data/passages', { params: { all: '1' } });
        if (!cancelled) {
          setPassages(res.data.passages || []);
        }
      } catch (err) {
        if (!cancelled) {
          setPassagesError(
            err.response?.data?.error || err.response?.data?.details || 'Failed to load passages'
          );
          setPassages([]);
        }
      } finally {
        if (!cancelled) setPassagesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadBundle = useCallback(async (pid) => {
    setLoadLoading(true);
    setLoadError('');
    setMessage({ type: '', text: '' });
    try {
      const res = await api.get('/data/translation-questions/bundle', { params: { passageId: pid } });
      const list = res.data?.rows || [];
      setRows(list.map((r) => normalizeRow(r)));
    } catch (err) {
      setRows([]);
      setLoadError(err.response?.data?.error || err.response?.data?.details || 'Failed to load data');
    } finally {
      setLoadLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!passageId) {
      setRows([]);
      setLoadError('');
      return;
    }
    const pid = parseInt(passageId, 10);
    loadBundle(pid);
  }, [passageId, loadBundle]);

  const updateRow = useCallback((index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      if (field === 'level') {
        const n = parseInt(value, 10);
        next[index] = { ...next[index], level: Number.isNaN(n) ? 1 : n };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  }, []);

  const hasSavedTranslationQuestions = (list) =>
    list.some((r) => r.question_id != null && String(r.question_id).trim() !== '');

  const handleAutoGenerate = async (e) => {
    e.preventDefault();
    if (!passageId) {
      setMessage({ type: 'error', text: 'Select a passage.' });
      return;
    }
    if (hasSavedTranslationQuestions(rows)) {
      const ok = window.confirm(
        '此篇章在資料庫中已有儲存過的譯文選擇題。按「確定」將產生新題目，並覆寫下方目前顯示的題幹與選項（寫入資料庫需再按「Save to database」）。按「取消」則不產生。'
      );
      if (!ok) {
        return;
      }
    }
    setGenLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const pid = parseInt(passageId, 10);
      const targets = rows.filter(hasTranslation);
      if (targets.length === 0) {
        setMessage({ type: 'error', text: 'No translated rows to generate from.' });
        return;
      }

      for (let i = 0; i < targets.length; i += GENERATE_BATCH_SIZE) {
        const batch = targets.slice(i, i + GENERATE_BATCH_SIZE);
        const doneBefore = i;
        setMessage({
          type: '',
          text: `Generating ${doneBefore} / ${targets.length}...`,
        });
        const res = await api.post('/data/translation-questions/generate', {
          passageId: pid,
          sentenceIds: batch.map((r) => r.sentence_id),
        });
        const fromServer = res.data?.rows || [];
        setRows((prev) => mergeGeneratedRows(prev, fromServer));
        setMessage({
          type: '',
          text: `Generating ${Math.min(i + batch.length, targets.length)} / ${targets.length}...`,
        });
      }
      setMessage({ type: 'success', text: 'Generation finished. Review and save.' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.details || err.response?.data?.error || 'Failed to generate',
      });
    } finally {
      setGenLoading(false);
    }
  };

  const performSaveTranslationQuestions = async () => {
    if (!passageId) {
      setMessage({ type: 'error', text: 'Select a passage.' });
      return false;
    }
    const toSave = rows.filter(
      (r) =>
        r.question?.trim() &&
        r.correct?.trim() &&
        r.wrong_1?.trim() &&
        r.wrong_2?.trim()
    );
    if (toSave.length === 0) {
      setMessage({
        type: 'error',
        text: 'No complete rows: fill question, correct, wrong_1, and wrong_2 (or generate first).',
      });
      return false;
    }
    setSaveLoading(true);
    setMessage({ type: '', text: '' });
    try {
      await api.post('/data/translation-questions/save', {
        items: toSave.map((r) => ({
          sentence_id: r.sentence_id,
          value: r.value == null ? '' : String(r.value),
          translation: r.translation == null ? '' : String(r.translation),
          question: r.question.trim(),
          correct: r.correct.trim(),
          wrong_1: r.wrong_1.trim(),
          wrong_2: r.wrong_2.trim(),
          level: r.level,
        })),
      });
      setMessage({ type: 'success', text: `Saved ${toSave.length} row(s).` });
      await loadBundle(parseInt(passageId, 10));
      return true;
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || err.response?.data?.details || 'Failed to save',
      });
      return false;
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await performSaveTranslationQuestions();
  };

  const handleSaveAndGoPassage = async () => {
    const ok = await performSaveTranslationQuestions();
    if (ok) navigate('/passage');
  };

  const hasRows = rows.length > 0;
  const hasPersistedRows = hasSavedTranslationQuestions(rows);

  return (
    <div className="dashboard">
      <DashboardHeader title="Translation question" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 1100, margin: '0 auto' }}>
          {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

          <div className="form-section">
            <h2>Translation questions</h2>

            <div className="form-group">
              <PassagePicker
                inputId="tq-passage-picker"
                label={
                  <>
                    Passage <span className="required">*</span>
                  </>
                }
                passages={passages}
                passagesLoading={passagesLoading}
                passagesError={passagesError}
                value={passageId}
                onChange={setPassageId}
              />
            </div>

            {passageId && loadLoading && <div className="loading-state">Loading…</div>}
            {passageId && !loadLoading && loadError && <div className="message error">{loadError}</div>}

            {passageId && !loadLoading && hasRows && (
              <form
                onSubmit={(ev) => {
                  ev.preventDefault();
                  handleSave(ev);
                }}
              >
                <p className="form-hint" style={{ marginBottom: 16 }}>
                  Each block is one sentence: edit value (source) and translation, then question and options. Use Auto
                  generate to fill question and options from translation where it exists.
                </p>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
                  <button
                    type="button"
                    className="submit-button"
                    onClick={handleAutoGenerate}
                    disabled={genLoading || saveLoading}
                  >
                    {genLoading ? 'Generating…' : 'Auto generate'}
                  </button>
                </div>

                {rows.map((row, index) => (
                  <div key={row.sentence_id} style={rowShellStyle}>
                    <label htmlFor={`tq-value-${row.sentence_id}`} style={titleLabelStyle}>
                      Sentence {index + 1}
                      {!hasTranslation(row) && (
                        <span style={{ color: '#999', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                          （無譯文，不參與產生）
                        </span>
                      )}
                    </label>

                    <AutosizeMonoTextarea
                      id={`tq-value-${row.sentence_id}`}
                      value={row.value == null ? '' : String(row.value)}
                      onChange={(v) => updateRow(index, 'value', v)}
                      disabled={genLoading || saveLoading}
                      ariaLabel={`value sentence ${index + 1}`}
                    />

                    <div className="form-group" style={{ marginTop: 12, marginBottom: 12 }}>
                      <SubFieldLabel htmlFor={`tq-translation-${row.sentence_id}`}>translation</SubFieldLabel>
                      <AutosizeMonoTextarea
                        id={`tq-translation-${row.sentence_id}`}
                        value={row.translation == null ? '' : String(row.translation)}
                        onChange={(v) => updateRow(index, 'translation', v)}
                        disabled={genLoading || saveLoading}
                        ariaLabel={`translation sentence ${index + 1}`}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <SubFieldLabel htmlFor={`tq-question-${row.sentence_id}`}>question</SubFieldLabel>
                      <input
                        id={`tq-question-${row.sentence_id}`}
                        type="text"
                        value={row.question ?? ''}
                        onChange={(e) => updateRow(index, 'question', e.target.value)}
                        disabled={genLoading || saveLoading}
                        style={textInputStyle}
                        aria-label={`question sentence ${index + 1}`}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <SubFieldLabel htmlFor={`tq-correct-${row.sentence_id}`}>correct</SubFieldLabel>
                      <input
                        id={`tq-correct-${row.sentence_id}`}
                        type="text"
                        value={row.correct ?? ''}
                        onChange={(e) => updateRow(index, 'correct', e.target.value)}
                        disabled={genLoading || saveLoading}
                        style={textInputStyle}
                        aria-label={`correct sentence ${index + 1}`}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <SubFieldLabel htmlFor={`tq-wrong1-${row.sentence_id}`}>wrong_1</SubFieldLabel>
                      <input
                        id={`tq-wrong1-${row.sentence_id}`}
                        type="text"
                        value={row.wrong_1 ?? ''}
                        onChange={(e) => updateRow(index, 'wrong_1', e.target.value)}
                        disabled={genLoading || saveLoading}
                        style={textInputStyle}
                        aria-label={`wrong_1 sentence ${index + 1}`}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <SubFieldLabel htmlFor={`tq-wrong2-${row.sentence_id}`}>wrong_2</SubFieldLabel>
                      <input
                        id={`tq-wrong2-${row.sentence_id}`}
                        type="text"
                        value={row.wrong_2 ?? ''}
                        onChange={(e) => updateRow(index, 'wrong_2', e.target.value)}
                        disabled={genLoading || saveLoading}
                        style={textInputStyle}
                        aria-label={`wrong_2 sentence ${index + 1}`}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <SubFieldLabel htmlFor={`tq-level-${row.sentence_id}`}>level</SubFieldLabel>
                      <input
                        id={`tq-level-${row.sentence_id}`}
                        type="number"
                        min={1}
                        value={row.level}
                        onChange={(e) => updateRow(index, 'level', e.target.value)}
                        disabled={genLoading || saveLoading}
                        style={{ ...textInputStyle, maxWidth: 100 }}
                        aria-label={`level sentence ${index + 1}`}
                      />
                    </div>
                  </div>
                ))}

                <p className="form-hint" style={{ marginTop: 0, marginBottom: 0 }}>
                  Level defaults to 1. Save only sends rows with all of question, correct, wrong_1, wrong_2 filled; for
                  those rows, value and translation are written to the sentence table too.
                </p>

                <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button type="submit" className="submit-button" disabled={saveLoading}>
                    {saveLoading ? 'Saving…' : 'Insert and Save'}
                  </button>
                  <button
                    type="button"
                    className="submit-button"
                    disabled={saveLoading}
                    onClick={handleSaveAndGoPassage}
                  >
                    {saveLoading ? 'Saving…' : 'Insert and Save, then go to Passage'}
                  </button>
                </div>
              </form>
            )}

            {passageId && !loadLoading && !loadError && !hasRows && (
              <div className="message error">No sentences for this passage.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranslationQuestion;
